import {cs}             from "@Common/react/chain-services";
import {fragments}      from "@Common/react/fragments";
import {Invoke}         from "@Common/react/invoke";
import {UseState}       from "@Common/react/use-state";
import {consumeContext} from "@Common/react/context";
import {split}          from "@Common/utils/collections";

const maxBatchSize = 5;

interface Feed {
    address: string;
    city: string;
    count: string;
    display_address: string;
    expectedCount: string;
    max: string;
    melissaCount: string;
    min: string;
    sort_house: string;
    sort_street: string;
    state: string;
    zip_code: string;
  }

export const GradualLoader = ({fetch, getKey, next}) => cs(
    consumeContext("systemConstants"),
    ["loadeds", (_, next) => UseState({initValue: [], next})], // [{key, feed, result, error}]
    ({systemConstants, loadeds}) => next({
        Load: ({feeds, next}) => cs(
            ["cal", (_, next) => next((() => {
                const [unloadeds, loadeds1] = split(feeds, (feed) => !getRecord(feed, {loadeds: loadeds.value, getKey}));
                return {unloadeds, loadeds: loadeds1};
            })())],
            ["state", (_, next) => UseState({next})],
            ({state, cal}) => fragments(
                next({
                    loading: !!cal.unloadeds.length && cal.unloadeds,
                    value: cal.loadeds.map((feed) => getRecord(feed, {loadeds: loadeds.value, getKey})),
                }),
                !state.value && !!cal.unloadeds.length && (
                    Invoke({
                        fn: async () => {
                            state.onChange(true);
                            
                            let result = [...loadeds.value];
                            const handleResults = (batchResults) => {
                                result.push(...batchResults);
                                loadeds.onChange(result);
                            };

                            await queueFeeds({
                                feeds: cal.unloadeds,
                                maxConcurrent: +systemConstants.value.MELISSA_MAX_CONCURRENT_CALLS,
                                fetch,
                                getKey,
                                handleResults,
                            });
                            state.onChange(false);
                        },
                    })
                ),
            ),
        ),
        loadAll: async ({feeds, searchForm=undefined, loading=undefined}) => {
            let searchError = false;
            const [unloadeds, loadeds1] = split(feeds, (feed) => !getRecord(feed, {loadeds: loadeds.value, getKey, searchForm}));

            let result = [...loadeds1.map((feed) => getRecord(feed, {loadeds: loadeds.value, getKey, searchForm}))];

            let loadedAddresses = 0;
            const addressesToLoad = unloadeds.reduce((acc: number, feed: Feed) => acc + +feed.count, 0);
            if(addressesToLoad && loading) {
                loading.onChange({
                    loading: true,
                    progress: {
                        done: loadedAddresses,
                        total: addressesToLoad
                    }
                });
            }
            
            const handleResults = (batchResults) => {
                if (batchResults) {
                    loadedAddresses += batchResults.reduce((acc, {feed}) => acc + +feed.count, 0);
                    loading && loading.onChange({
                        loading: true,
                        progress: {
                            done: loadedAddresses,
                            total: addressesToLoad
                        }
                    });
                    result.push(...batchResults);
                } else {
                    searchError = true;
                }
            };

            await queueFeeds({
                feeds: unloadeds,
                maxConcurrent: +systemConstants.value.MELISSA_MAX_CONCURRENT_CALLS,
                fetch,
                searchForm,
                getKey,
                handleResults,
            });

            loadeds.onChange(result);
            loading && loading.onChange({loading: false});
            if (searchError) {
                return {result: "failed"};
            }
            return result;
        },
        loadeds: loadeds.value,
    }),
);

const queueFeeds = async ({feeds, maxConcurrent, fetch, searchForm=undefined, getKey, handleResults}) => {
    let batchSize = maxBatchSize;
    if (feeds.length < maxBatchSize * maxConcurrent) {
        batchSize = Math.ceil(feeds.length / maxConcurrent);
    }

    let batches = []
    for (let i = 0; i < feeds.length; i += batchSize) {
        batches.push(feeds.slice(i, i + batchSize));
    }

    let promises = [];
    let addingToQueue = true;
    const fetchNextBatch = async () => {
        const batch = batches.shift();
        if (batch) {
            promises.push(
                fetch({feeds: batch, searchForm}).then((batchResults) => {
                    handleResults(batchResults && [
                        ...batch.map((feed, i) => ({
                            key: getKey({feed, searchForm}),
                            feed,
                            result: batchResults[i],
                        }))
                    ]);
                    fetchNextBatch();
                })
            );
        } else {
            addingToQueue = false;
        }
    };

    for (let i = 0; i < maxConcurrent; i++) {
        fetchNextBatch();
    }

    const checkQueue = async () => {
        if (addingToQueue) {
            await new Promise(resolve => setTimeout(resolve, 500))
            await checkQueue();
        } else {
            await Promise.all(promises);
        }
    };

    await checkQueue();
}

const getRecord = (feed, {loadeds, getKey, searchForm=undefined}) => {
    const key = getKey({feed, searchForm});
    return loadeds.find((loaded) => loaded.key === key);
};
